import { z } from 'zod';
import { pageSchema } from '../../http/pagination';

// ISO 8601形式の日付バリデーション用スキーマ
// クエリパラメータのdateFromやdateToなど、optionalな日付文字列をDateに変換
// フルフォーマット（YYYY-MM-DDTHH:mm:ss.sssZ）を受け付ける
export const optionalDateSchema = z
  .string()
  .optional()
  .transform((val, ctx) => {
    if (val === undefined) {
      return undefined;
    }
    // ISO 8601フルフォーマット（YYYY-MM-DDTHH:mm:ss.sssZ）を受け付ける
    const date = new Date(val);
    if (Number.isNaN(date.getTime())) {
      ctx.addIssue({
        code: 'custom',
        message: 'Date must be a valid ISO 8601 date',
      });
      return z.NEVER;
    }
    return date;
  });

export const listRecordingsQuerySchema = z
  .object({
    page: pageSchema,
    customerName: z.string().min(1).optional(),
    // 部分一致検索のため 1〜11 桁の数字のみ。上限 11 桁は、顧客登録時のバリデーション
    // （customers/schema.ts の PHONE_PATTERN = /^\d{10,11}$/）により phone / mobilePhone が
    // 数字のみ 10〜11 桁で保存されることに由来する。frontend の PHONE_SEARCH_PATTERN
    // （shared/phonePattern.ts）と桁数を一致させること。
    customerPhone: z
      .string()
      .regex(/^\d{1,11}$/, 'Customer phone must be 1-11 digits')
      .optional(),
    advisorName: z.string().min(1).optional(),
    recordingStatusId: z.coerce.number().int().min(1).max(3).optional(),
    dateFrom: optionalDateSchema,
    dateTo: optionalDateSchema,
  })
  .refine(
    data => {
      if (data.dateFrom && data.dateTo) {
        return data.dateFrom <= data.dateTo;
      }
      return true;
    },
    {
      message: 'dateFrom must be less than or equal to dateTo',
      path: ['dateFrom'],
    }
  );
export type ListRecordingsQuery = z.infer<typeof listRecordingsQuerySchema>;

export const callDirectionSchema = z.enum(['outgoing', 'incoming', 'unknown']);
export type CallDirection = z.infer<typeof callDirectionSchema>;

export const recordingSchema = z.object({
  id: z.number(),
  advisorId: z.number().nullable(),
  advisorName: z.string().nullable(),
  customerId: z.number().nullable(),
  customerName: z.string().nullable(),
  title: z.string(),
  duration: z.number().nullable(),
  recordingStatusId: z.number(),
  recordingStatusName: z.string(),
  callDirection: callDirectionSchema,
  meetingDateTime: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Recording = z.infer<typeof recordingSchema>;

export const listRecordingsResponseSchema = z.object({
  count: z.number(),
  recordings: z.array(recordingSchema),
});
export type ListRecordingsResponse = z.infer<typeof listRecordingsResponseSchema>;

export const recordingDetailSchema = z.object({
  id: z.number(),
  advisorId: z.number().nullable(),
  advisorName: z.string().nullable(),
  advisorPhone: z.string().nullable(),
  customerId: z.number().nullable(),
  customerName: z.string().nullable(),
  customerPhone: z.string().nullable(),
  customerMobilePhone: z.string().nullable(),
  originalFrom: z.string(),
  originalTo: z.string(),
  title: z.string(),
  duration: z.number(),
  processingStatus: z.string(),
  meetingDateTime: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type RecordingDetail = z.infer<typeof recordingDetailSchema>;

export const updateRecordingSchema = z.object({
  title: z.string().min(1, 'Title is required').max(255, 'Title must be 255 characters or less'),
  advisorId: z.number().int().positive('Advisor ID must be a positive integer'),
  customerId: z.number().int().positive('Customer ID must be a positive integer'),
  meetingDateTime: z.coerce.date(),
});
export type UpdateRecordingBody = z.infer<typeof updateRecordingSchema>;

export const updateRecordingResponseSchema = z.object({
  id: z.number(),
  title: z.string(),
  customer: z.object({
    id: z.number(),
    name: z.string(),
  }),
  advisor: z.object({
    id: z.number(),
    name: z.string(),
  }),
  meetingDateTime: z.coerce.date(),
});
export type UpdateRecordingResponse = z.infer<typeof updateRecordingResponseSchema>;

export const fullTextSegmentSchema = z.object({
  caption: z.string(),
  speaker: z.string(),
  time_code: z.string(),
});
export type FullTextSegment = z.infer<typeof fullTextSegmentSchema>;

export const fullTextSchema = z.array(fullTextSegmentSchema);
export type FullText = z.infer<typeof fullTextSchema>;

export const recordingResponseSchema = z.object({
  id: z.number(),
  customer: z
    .object({
      id: z.number(),
      name: z.string(),
      phone: z.string().nullable(),
      mobilePhone: z.string().nullable(),
    })
    .nullable(),
  advisor: z
    .object({
      id: z.number(),
      name: z.string(),
      phone: z.string(),
    })
    .nullable(),
  originalFrom: z.string().nullable(),
  originalTo: z.string().nullable(),
  title: z.string(),
  duration: z.number().nullable(),
  registeredAt: z.coerce.date(),
  meetingDateTime: z.coerce.date(),
  summaryText: z.string().nullable(),
  fullText: fullTextSchema.nullable(),
  presignedUrl: z.string(),
  recordingStatusId: z.number(),
  recordingStatusName: z.string(),
  sourceId: z.number(),
});
export type RecordingResponse = z.infer<typeof recordingResponseSchema>;

export const retryRecordingResponseSchema = z.object({
  id: z.number(),
  recordingStatusId: z.number(),
});
export type RetryRecordingResponse = z.infer<typeof retryRecordingResponseSchema>;

export const presignedUrlResponseSchema = z.object({
  presignedUrl: z.string(),
});
export type PresignedUrlResponse = z.infer<typeof presignedUrlResponseSchema>;

export const updateSummarySchema = z.object({
  summaryText: z.string().min(1, 'Summary text is required'),
});
export type UpdateSummaryBody = z.infer<typeof updateSummarySchema>;

export const updateSummaryResponseSchema = z.object({
  id: z.number(),
  summaryText: z.string(),
  updatedAt: z.coerce.date(),
});
export type UpdateSummaryResponse = z.infer<typeof updateSummaryResponseSchema>;

export const createRecordingSchema = z.object({
  title: z.string().min(1, 'Title is required').max(255, 'Title must be 255 characters or less'),
  customerId: z.number().int().positive('Customer ID must be a positive integer'),
  advisorId: z.number().int().positive('Advisor ID must be a positive integer'),
  meetingDateTime: z.coerce.date(),
});
export type CreateRecordingBody = z.infer<typeof createRecordingSchema>;

export const createRecordingResponseSchema = z.object({
  recordingId: z.number(),
  presignedUrl: z.string(),
  filePath: z.string(),
});
export type CreateRecordingResponse = z.infer<typeof createRecordingResponseSchema>;
